import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, ShieldCheck, Sparkles, ArrowLeft, IndianRupee, Heart, Zap, Info } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RazorpayCheckoutButton from "@/components/RazorpayCheckoutButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { toast } from "sonner";

// ---------- Plan data ----------
const PLANS = {
  free: {
    id: "free",
    name: "Free",
    price: 0,
    tagline: "Try InsureGenie risk-free",
    features: [
      "10 AI chats per month",
      "Upload 1 policy document",
      "Basic policy summaries",
      "IRDAI & insurer citations",
      "Community support",
    ],
  },
  pro: {
    id: "pro",
    name: "Pro",
    price: 299,
    tagline: "For serious insurance decisions",
    features: [
      "Unlimited AI chats",
      "Unlimited document uploads (PDF, Word, images)",
      "Deep policy comparison tables",
      "Section 80C / 80D / 10(10D) tax planner",
      "Priority Claude Sonnet 4.5 model — faster answers",
      "Global KB access (35+ Indian insurer sources)",
      "Priority email support — 24-hour response",
      "Exclusive: quarterly policy audit report",
    ],
  },
};

// Feature comparison rows
const COMPARISON = [
  { label: "AI chats per month", free: "10", pro: "Unlimited" },
  { label: "Document uploads", free: "1", pro: "Unlimited" },
  { label: "Supported formats", free: "PDF only", pro: "PDF · Word · Images" },
  { label: "Policy summaries", free: true, pro: true },
  { label: "IRDAI & insurer citations", free: true, pro: true },
  { label: "Deep multi-policy comparison", free: false, pro: true },
  { label: "Tax planner (80C / 80D / 10(10D))", free: false, pro: true },
  { label: "Priority Sonnet 4.5 (faster)", free: false, pro: true },
  { label: "Global knowledge base (35+ insurers)", free: "Preview", pro: "Full access" },
  { label: "Quarterly policy audit", free: false, pro: true },
  { label: "Email support SLA", free: "Best-effort", pro: "24 hours" },
  { label: "WhatsApp support", free: false, pro: true },
];

// ---------- Component ----------
export default function Pricing() {
  useSEO({
    title: "Pricing — InsureGenie India | Free & Pro ₹299/month",
    description: "Simple, transparent pricing for AI insurance guidance in India. Free tier + Pro (₹299/mo) with unlimited chats, unlimited documents and full IRDAI knowledge base.",
  });
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [payments, setPayments] = useState([]);
  const [donationAmount, setDonationAmount] = useState("199");
  const [donationMessage, setDonationMessage] = useState("");
  const [donationBusy, setDonationBusy] = useState(false);
  const [testMode, setTestMode] = useState(false);

  const isPro = user?.plan === "pro";

  useEffect(() => {
    api.get("/payments/config")
      .then(({ data }) => setTestMode(!!data.test_mode))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/payments/history?limit=8")
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => {});
  }, [user]);

  const handleProUpgradeSuccess = async () => {
    await refresh?.();
    toast.success("Welcome to Pro! 🎉", { description: "All Pro features are unlocked for the next 30 days." });
    api.get("/payments/history?limit=8").then(({ data }) => setPayments(data.payments || []));
  };

  const donate = async () => {
    const amt = Number(donationAmount);
    if (!amt || amt < 10) {
      toast.error("Minimum donation is ₹10");
      return;
    }
    setDonationBusy(true);
    try {
      const { data } = await api.post("/payments/donate", { amount: amt, message: donationMessage });
      // Open Razorpay modal
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "InsureGenie", description: "Donation to support the platform",
        order_id: data.order_id,
        prefill: { email: user.email, name: user.name || "" },
        theme: { color: "#14B8A6" },
        modal: { ondismiss: () => setDonationBusy(false) },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", resp);
            toast.success("Thank you for your support! 💚", { description: `₹${amt} received.` });
            api.get("/payments/history?limit=8").then(({ data }) => setPayments(data.payments || []));
            setDonationMessage("");
          } catch (e) {
            toast.error("Donation not verified", { description: formatApiError(e) });
          } finally { setDonationBusy(false); }
        },
      });
      rzp.on("payment.failed", (r) => { setDonationBusy(false); toast.error("Payment failed", { description: r?.error?.description }); });
      rzp.open();
    } catch (e) {
      setDonationBusy(false);
      toast.error("Could not start donation", { description: formatApiError(e) });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 backdrop-blur bg-background/80 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground" data-testid="pricing-back-home">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <Logo />
          </div>
          <div className="flex items-center gap-2">
            {isPro && (
              <Badge className="bg-secondary/10 text-secondary border border-secondary/30" data-testid="pro-active-badge">
                <Sparkles className="w-3 h-3 mr-1" /> PRO Active
              </Badge>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Badge className="rounded-full bg-accent/10 text-accent border border-accent/20 px-3 py-1 mb-4 font-medium">
            <span className="mr-1.5" role="img" aria-label="India">🇮🇳</span> Priced in ₹, for Indian customers
          </Badge>
          <h1 data-testid="pricing-headline" className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
            Free forever. Pro when you're ready.
          </h1>
          <p className="mt-4 text-muted-foreground text-lg">
            Ask 10 free questions every month. Upgrade to Pro for unlimited AI conversations and premium tools.
          </p>
        </div>

        {/* Test-mode helper banner */}
        {testMode && (
          <div
            data-testid="razorpay-test-mode-banner"
            className="max-w-4xl mx-auto mb-8 rounded-xl border border-amber-400/40 bg-amber-50 dark:bg-amber-950/30 px-5 py-4 text-sm"
          >
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="text-amber-900 dark:text-amber-100">
                <div className="font-semibold mb-1">Razorpay is in Test Mode — no real money will be charged.</div>
                <div className="text-[13px] leading-relaxed">
                  Real UPI apps (GPay, PhonePe, Paytm) <strong>will fail</strong> in test mode.
                  Please use one of these test methods:
                </div>
                <ul className="mt-2 space-y-1 text-[13px] leading-relaxed">
                  <li>
                    <strong>UPI (recommended):</strong> Choose UPI → enter <code className="px-1.5 py-0.5 bg-amber-200/60 dark:bg-amber-900/60 rounded font-mono text-[12px]">success@razorpay</code> as VPA → Verify → Pay.
                  </li>
                  <li>
                    <strong>Card:</strong> <code className="px-1.5 py-0.5 bg-amber-200/60 dark:bg-amber-900/60 rounded font-mono text-[12px]">4111 1111 1111 1111</code>, expiry any future date, CVV any 3 digits → Continue → on the simulator page click <strong>Success</strong>.
                  </li>
                  <li>
                    If Continue seems stuck, disable pop-up blockers and try again — the simulator opens in a new frame.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {[PLANS.free, PLANS.pro].map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={
                "relative rounded-2xl border p-8 flex flex-col " +
                (p.id === "pro"
                  ? "border-secondary/40 bg-gradient-to-br from-secondary/[0.06] to-transparent shadow-2xl shadow-secondary/10"
                  : "border-border bg-card")
              }
            >
              {p.id === "pro" && (
                <div className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-[11px] font-semibold shadow-sm">
                  <Zap className="w-3 h-3" /> Recommended
                </div>
              )}
              <div className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{p.name}</div>
              <div className="flex items-baseline gap-1">
                <IndianRupee className="w-6 h-6 text-foreground/70" />
                <span className="font-display text-6xl font-semibold tracking-tight">{p.price}</span>
                <span className="text-muted-foreground text-sm ml-1">/month</span>
              </div>
              <div className="mt-1 text-[13px] text-muted-foreground">{p.tagline}</div>

              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-secondary shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                {p.id === "free" ? (
                  <div className="text-center text-[13px] text-muted-foreground py-3 border border-dashed border-border/60 rounded-md">
                    You're on this plan — no card required
                  </div>
                ) : isPro ? (
                  <div className="text-center py-3 border border-secondary/30 bg-secondary/5 rounded-md text-[13px] font-medium text-secondary" data-testid="pro-current">
                    ✓ Pro is active until {user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "renewal"}
                  </div>
                ) : user ? (
                  <RazorpayCheckoutButton
                    amount={299}
                    purpose="pro-monthly"
                    description="InsureGenie Pro — 1 month"
                    buttonText="Upgrade to Pro"
                    prefill={{ email: user.email, name: user.name || "" }}
                    className="w-full h-12 bg-secondary hover:bg-secondary/90 text-secondary-foreground text-base"
                    onSuccess={handleProUpgradeSuccess}
                  />
                ) : (
                  <button
                    data-testid="plan-pro-signin"
                    onClick={() => navigate("/auth?next=/pricing")}
                    className="w-full h-12 rounded-md font-medium bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  >
                    Sign in to upgrade
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <section className="mt-20 max-w-4xl mx-auto" data-testid="comparison-section">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl font-semibold tracking-tight">Free vs Pro — line by line</h2>
            <p className="mt-2 text-muted-foreground">Every feature side by side so you can decide with clear eyes.</p>
          </div>
          <div className="rounded-2xl border border-border overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Feature</th>
                  <th className="px-5 py-3.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider text-center">Free</th>
                  <th className="px-5 py-3.5 font-semibold text-secondary text-xs uppercase tracking-wider text-center">Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-muted/20" : ""}>
                    <td className="px-5 py-3">{row.label}</td>
                    <td className="px-5 py-3 text-center">
                      {row.free === true ? <Check className="w-4 h-4 text-secondary inline" />
                        : row.free === false ? <X className="w-4 h-4 text-muted-foreground/40 inline" />
                        : <span className="text-muted-foreground">{row.free}</span>}
                    </td>
                    <td className="px-5 py-3 text-center font-medium">
                      {row.pro === true ? <Check className="w-4 h-4 text-secondary inline" />
                        : row.pro === false ? <X className="w-4 h-4 text-muted-foreground/40 inline" />
                        : <span className="text-secondary">{row.pro}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Donation section */}
        <section className="mt-20 max-w-2xl mx-auto" data-testid="donation-section">
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.05] to-transparent p-8">
            <div className="flex items-center gap-2 text-accent font-semibold text-sm uppercase tracking-wide">
              <Heart className="w-4 h-4" /> Support InsureGenie
            </div>
            <h2 className="font-display text-2xl font-semibold tracking-tight mt-2">Not ready for Pro? Chip in what you can.</h2>
            <p className="mt-2 text-muted-foreground text-sm">
              InsureGenie is built to keep insurance guidance free for the majority of Indian families. Any donation directly funds keeping the free tier alive and expanding the IRDAI + insurer knowledge base.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3">
              <div className="relative">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="donation-amount"
                  type="number"
                  min="10"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  className="pl-9 h-11"
                  placeholder="Amount"
                />
              </div>
              <Input
                data-testid="donation-message"
                value={donationMessage}
                onChange={(e) => setDonationMessage(e.target.value)}
                className="h-11"
                placeholder="Optional message (280 chars)"
                maxLength={280}
              />
              {user ? (
                <Button
                  data-testid="donate-btn"
                  onClick={donate}
                  disabled={donationBusy || !donationAmount || Number(donationAmount) < 10}
                  className="h-11 bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                >
                  <Heart className="w-4 h-4" /> Donate
                </Button>
              ) : (
                <Button onClick={() => navigate("/auth?next=/pricing")} className="h-11 gap-2">
                  <Heart className="w-4 h-4" /> Sign in to donate
                </Button>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {[99, 199, 499, 999, 1999].map((v) => (
                <button
                  key={v}
                  onClick={() => setDonationAmount(String(v))}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-border hover:border-accent/50 hover:bg-accent/10 transition-colors"
                >
                  ₹{v}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-14 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
          <ShieldCheck className="w-4 h-4 text-secondary" />
          Payments processed securely by{" "}
          <a href="https://razorpay.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Razorpay</a>.
          UPI, cards, netbanking, wallets — all accepted.
        </div>

        {payments.length > 0 && (
          <div className="mt-14 max-w-2xl mx-auto">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Your recent payments</h2>
            <div className="rounded-xl border border-border divide-y divide-border/60 bg-card">
              {payments.map((p) => (
                <div key={p.razorpay_order_id} className="p-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{p.purpose === "donation" ? "❤️ Donation" : p.purpose || "Payment"}</div>
                    <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">₹{(p.amount / 100).toFixed(0)}</div>
                    <div className={"text-[11px] uppercase font-semibold " + (p.status === "paid" ? "text-emerald-600" : "text-muted-foreground")}>
                      {p.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
